import { EnvVars } from '../../../environment';
import type { SearchableComboboxOption } from '@/new/components/searchable-combobox';

// `Intl.supportedValuesOf` is a 2022 spec; modern Chromium/Firefox/Safari ship
// it. We type-cast because TS 4.9 doesn't expose the signature on `Intl`.
const intlGlobal = Intl as typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

// Small curated fallback if the runtime can't enumerate IANA zones.
const FALLBACK_TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Dubai',
  'Australia/Sydney',
];

/**
 * Per-deployment timezone default. `primary` is the fallback if the user's
 * browser zone doesn't belong to the region; `matchers` define what "belongs
 * to the region" means (a browser zone matching any of them is kept verbatim
 * so e.g. a California user on the US deployment doesn't get pinned to NY).
 */
const ENV_TIMEZONE_DEFAULTS: Record<string, { primary: string; matchers: RegExp[] }> = {
  'production-uk': {
    primary: 'Europe/London',
    matchers: [/^Europe\/(London|Belfast|Isle_of_Man|Jersey|Guernsey)$/],
  },
  'production-us': {
    primary: 'America/New_York',
    matchers: [/^America\//, /^US\//, /^Pacific\/Honolulu$/],
  },
  'production-au': {
    primary: 'Australia/Sydney',
    matchers: [/^Australia\//],
  },
  'production-eu': {
    primary: 'Europe/Paris',
    matchers: [/^Europe\//, /^Atlantic\//],
  },
};

export function listTimeZones(): string[] {
  const supported = intlGlobal.supportedValuesOf?.('timeZone');
  if (supported && supported.length > 0) return supported;
  return FALLBACK_TIME_ZONES;
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Resolve the default timezone for the current deployment:
 *  - For a region-specific production env (UK / US / AU / EU): keep the
 *    browser zone if it falls inside that region, otherwise fall back to the
 *    region's primary zone.
 *  - For `local` / `staging` (or any env we don't recognise): use the browser
 *    zone, falling back to UTC.
 */
export function detectTimeZone(): string {
  const browser = browserTimeZone();
  const envDefault = ENV_TIMEZONE_DEFAULTS[EnvVars.ENVIRONMENT];

  if (envDefault) {
    if (browser && envDefault.matchers.some((m) => m.test(browser))) return browser;
    return envDefault.primary;
  }

  return browser ?? 'UTC';
}

/**
 * Current UTC offset for an IANA zone in the form "+01:00", "-04:00", or
 * "+00:00". Returns null if the runtime can't resolve the zone. Computed at
 * "now", so DST is reflected: the same zone may render with a different
 * offset across the year.
 */
export function getTimeZoneOffset(zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (raw === undefined) return null;
    if (raw === 'GMT') return '+00:00';
    return raw.replace(/^GMT/, '');
  } catch {
    return null;
  }
}

// Curated, region-weighted shortlist shown in the picker instead of the full
// ~400-entry IANA set.
const CURATED_TIME_ZONES = [
  'UTC',
  // Europe
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  // United States (all six mainland + Alaska/Hawaii zones; Phoenix covers
  // Arizona, which doesn't observe DST)
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  // Canada (all six zones; St John's is the +/-:30 Newfoundland offset,
  // Halifax is Atlantic, Winnipeg Central, Edmonton Mountain)
  'America/St_Johns',
  'America/Halifax',
  'America/Toronto',
  'America/Winnipeg',
  'America/Edmonton',
  'America/Vancouver',
  // Latin America
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  // Africa & Middle East
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Asia/Jerusalem',
  'Asia/Dubai',
  // Asia
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  // Australia & NZ (distinct state clocks: Perth is AWST, Darwin/Adelaide are
  // the +9:30 Central zones — Darwin no DST, Adelaide with DST — Brisbane is
  // eastern with no DST, Sydney is eastern with DST and covers Melbourne/Hobart)
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Auckland',
];

// Friendly continent/region word for the leading IANA path segment.
const REGION_LABELS: Record<string, string> = {
  Africa: 'Africa',
  America: 'Americas',
  Antarctica: 'Antarctica',
  Arctic: 'Arctic',
  Asia: 'Asia',
  Atlantic: 'Atlantic',
  Australia: 'Oceania',
  Europe: 'Europe',
  Indian: 'Indian Ocean',
  Pacific: 'Oceania',
};

/** The curated list with the current deployment region's zones floated first. */
function curatedTimeZones(): string[] {
  const envDefault = ENV_TIMEZONE_DEFAULTS[EnvVars.ENVIRONMENT];
  if (!envDefault) return CURATED_TIME_ZONES;
  const inRegion = CURATED_TIME_ZONES.filter((z) => envDefault.matchers.some((m) => m.test(z)));
  const rest = CURATED_TIME_ZONES.filter((z) => !inRegion.includes(z));
  return [...inRegion, ...rest];
}

/**
 * User-friendly display label, e.g. `"New York, Americas (UTC-04:00)"`. Works
 * for any IANA id (not just the curated set), so saved/extracted zones outside
 * the shortlist still render sensibly. The offset is DST-aware ("now").
 */
export function formatTimeZoneLabel(zone: string): string {
  const offset = getTimeZoneOffset(zone);
  const suffix = offset !== null ? ` (UTC${offset})` : '';

  const slash = zone.indexOf('/');
  if (slash === -1) return `${zone}${suffix}`; // e.g. "UTC" — no region segment.

  const region = REGION_LABELS[zone.slice(0, slash)] ?? zone.slice(0, slash);
  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  return `${city}, ${region}${suffix}`;
}

/**
 * Options for the timezone picker: the curated, region-weighted shortlist. Pass
 * `include` (e.g. the currently-selected/saved zone) to guarantee that value is
 * present and selectable even when it falls outside the shortlist.
 */
export function getTimeZoneOptions(include?: string | null): SearchableComboboxOption[] {
  const zones = curatedTimeZones();
  const withSelected = include && !zones.includes(include) ? [include, ...zones] : zones;
  return withSelected.map((tz) => ({ label: formatTimeZoneLabel(tz), value: tz }));
}
